/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, Output, ViewChild } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { GoogleMapsLoaderService, IGoogleSearchAddress} from '@sml/utility';
import { IconsComponent } from '@sml/icons';
import { FormControl, ReactiveFormsModule,NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { environment as env} from '@env/environment';

declare let google: any;

@Component({
  selector: 'sml-google-places-autocomplete',
  imports: [CommonModule,ReactiveFormsModule,IconsComponent],
  templateUrl: './google-places-autocomplete.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: GooglePlacesAutocompleteComponent,
      multi: true,
    },
  ],
})
export class GooglePlacesAutocompleteComponent implements AfterViewInit, ControlValueAccessor {
  @ViewChild('googleAutocompletPlaceSearch', { static: false }) searchElementRef!: ElementRef;
  @Output() placeSelected: EventEmitter<any> = new EventEmitter();

  @Input() addressControl = new FormControl('');
  @Input() address!: string | null;
  @Input() isInvalid = false;
  @Input() showLocationIcon = false;
  public addressValue = '';
  public addressModel = {} as IGoogleSearchAddress;

  private onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(
    private readonly googleMapsLoader: GoogleMapsLoaderService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  ngAfterViewInit(): void {
    const apikey = env.googleAPI;
    this.loadGoogleMapsApi(apikey);
  }

  loadGoogleMapsApi(apiKey: string): void {
    this.googleMapsLoader.loadGoogleMapsApi(apiKey).subscribe({
      next: (google) => {
        this.initAutocomplete(google);
      },
      error: (err) => {
        console.error('Error loading Google Maps API:', err);
      },
    });
  }

  initAutocomplete(google: any): void {
    if (this.searchElementRef) {
      if (!this.isGoogleLibExists()) {
        throw new Error('Google maps library cannot be found');
      }

      const input = this.searchElementRef.nativeElement;
      const autocomplete = new google.maps.places.Autocomplete(input, {
        types: ['geocode'],
        componentRestrictions: { country: 'us' },
      });

      if (!autocomplete) {
        throw new Error('Autocomplete is not initialized');
      }

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        this.addressModel = {} as IGoogleSearchAddress;

        if (place.geometry) {
          this.addressModel.formattedAddress = place.formatted_address;
          this.addressModel.lat = place.geometry.location.lat();
          this.addressModel.long = place.geometry.location.lng();

          place.address_components.forEach((element: any) => {
            this.patchAddressModel(element);
          });

          this.updateFormattedAddress();
        }
      });

      input.addEventListener('input', (event: Event) => {
        const target = event.target as HTMLInputElement;
        if (!target.value) {
          this.addressModel = {...this.addressModel, formattedAddress: '', lat: undefined, long: undefined};
          this.updateFormattedAddress();
        }
      });
    }
  }

  private updateFormattedAddress() {
    this.addressValue = this.addressModel.formattedAddress || '';
    this.addressControl.setValue(this.addressModel.formattedAddress || '');
    this.onChange(this.addressValue);

    this.placeSelected.emit(this.addressModel);
  }

  patchAddressModel(element: any): void {
    if (element.types.includes('street_number')) {
      this.addressModel.addressLine1 = element.long_name;
    }
    if (element.types.includes('route')) {
        this.addressModel.addressLine1 = this.addressModel.addressLine1
            ? `${this.addressModel.addressLine1} ${element.long_name}`
            : element.long_name;
    }
    if (element.types.includes('premise') && !this.addressModel.addressLine1) {
      this.addressModel.addressLine1 = element.long_name; // Use premise if no street address
    }
    if (element.types.includes('subpremise')) {
        this.addressModel.addressLine2 = element.long_name; // Apartment/Suite number
    }
    if (element.types.includes('locality')) {
        this.addressModel.city = element.long_name;
    }
    if (element.types.includes('sublocality_level_1') && !this.addressModel.city) {
      this.addressModel.city = element.long_name; // Use if locality is missing
    }
    if (element.types.includes('administrative_area_level_1')) {
        this.addressModel.state = element.long_name;
        this.addressModel.stateShortName = element.short_name;
    }
    if (element.types.includes('postal_code')) {
        this.addressModel.zipCode = element.long_name;
    }
    if (element.types.includes('country')) {
        this.addressModel.country = element.long_name;
    }
  }

  private isGoogleLibExists(): boolean {
    return google && google.maps && google.maps.places;
  }

  writeValue(value: any): void {
    this.addressValue = value || '';
    this.addressControl.setValue(value || '');
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.addressControl.disable();
    } else {
      this.addressControl.enable();
    }
  }
}
